import React from 'react';
/**
 * The anatomy every booking-modal view shares: a header row (title and an
 * optional lede over a hairline), a body, and a slate footer band that always
 * holds the other way to reach Nathan. Form, calendar and confirmation all
 * render these, so switching views swaps only the body.
 *
 * Approved design: `.claude/design-specs/2026-11-01-talk-to-us-note-first.html`.
 * The modal keeps its unmigrated styling (Inter, Instrument Serif, slate,
 * `#B45309`); moving it onto the Part 1 system is separate work.
 *
 * @module
 */

import { CalendarIcon } from './BookingModal.icons';

export function ModalHeader({
  title,
  lede,
  focusable = false,
}: {
  title: string;
  lede?: string;
  /** The calendar and the confirmation move focus to their title (`tabIndex={-1}`) on entry. */
  focusable?: boolean;
}): React.ReactElement {
  return (
    <div className="flex-none border-b border-slate-200 py-4 pl-5 pr-[60px] sm:py-5 sm:pl-8 sm:pr-[72px]">
      <h3
        id="booking-modal-title"
        tabIndex={focusable ? -1 : undefined}
        className="font-display text-2xl font-normal leading-[1.15] text-slate-900 focus:outline-none sm:text-[28px]"
      >
        {title}
      </h3>
      {lede && <p className="mt-1.5 text-balance text-[15px] leading-snug text-slate-600">{lede}</p>}
    </div>
  );
}

export function ModalFooter({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div className={`flex flex-none items-center border-t border-slate-200 bg-slate-50 px-5 py-2 sm:px-8 ${className}`}>
      {children}
    </div>
  );
}

/**
 * The quiet footer link on the form and the confirmation, where a filled send
 * button already leads. A button, not a link: it changes the dialog's view.
 */
export function CallLink({ label, onClick }: { label: string; onClick: () => void }): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-slate-700 underline decoration-current decoration-1 underline-offset-4 transition-[color,text-decoration-color] duration-200 hover:text-slate-900 hover:decoration-transparent focus:outline-none focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B45309] motion-reduce:transition-none"
    >
      <CalendarIcon className="h-4 w-4 flex-none text-slate-500" />
      {label}
    </button>
  );
}
